'use client';

import { stegaClean } from '@sanity/client/stega';
import flags from 'react-phone-number-input/flags';

/**
 * Hero flag row. `isInternational` shows a globe next to the flags — for roles
 * that are not tied to a single market.
 *
 * Flags come from `react-phone-number-input/flags`, the same source the
 * PhoneInput uses. All 265 countries are covered out of the box, so adding a
 * market never needs a deploy or an upload — the editor just types the code.
 */

function GlobeIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="h-[3.5714rem] w-[3.5714rem] shrink-0 text-black"
      aria-hidden="true"
    >
      <circle cx="10" cy="10" r="8.25" stroke="currentColor" strokeWidth="1.3" />
      <ellipse cx="10" cy="10" rx="3.4" ry="8.25" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2.1 7.4h15.8M2.1 12.6h15.8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

export default function CountryFlags({
  countries,
  isInternational,
  className,
}: {
  countries?: string[];
  isInternational?: boolean;
  className?: string;
}) {
  const codes = (stegaClean(countries) ?? [])
    .map((code) => String(code).trim().toUpperCase())
    .filter((code) => /^[A-Z]{2}$/.test(code));

  if (!isInternational && codes.length === 0) return null;

  return (
    <div className={`flex items-center justify-center gap-[1.1905rem] ${className ?? ''}`}>
      {isInternational && <GlobeIcon />}
      {codes.map((code, index) => {
        const Flag = flags[code as keyof typeof flags];
        if (!Flag) return null;
        return (
          <span
            key={`${code}-${index}`}
            className="flex h-[3.5714rem] w-[5rem] shrink-0 items-center overflow-hidden [&_svg]:!h-full [&_svg]:!w-full"
          >
            <Flag title={code} />
          </span>
        );
      })}
    </div>
  );
}
